import { type DashboardAccessInput } from '@/lib/dashboards-api';
import { relativeTime } from '@/lib/relativeTime';
import getRouterBasename from '@/lib/router';
import { cn } from '@/lib/utils';
import {
    ChevronDown,
    ExternalLink,
    LayoutGrid,
    Pencil,
    Trash2,
    Users
} from 'lucide-react';
import { useState } from 'react';

import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';

import { ExportToBigDialog } from './ExportToBigDialog';
import { useDashboardExportState } from './useDashboardExportState';

interface DashboardMenuProps {
    dashboardId: string;
    title: string;
    updatedAt: string;
    onRename: (title: string) => Promise<void>;
    onDelete: () => Promise<void>;
    onExportToApps: (
        name: string,
        grouping: string,
        access: DashboardAccessInput | undefined
    ) => Promise<void>;
    shared: boolean;
    onToggleShare?: (shared: boolean) => Promise<void>;
}

export function DashboardMenu({
    dashboardId,
    title,
    updatedAt,
    onRename,
    onDelete,
    onExportToApps,
    shared,
    onToggleShare
}: DashboardMenuProps) {
    const [menuOpen, setMenuOpen] = useState(false);
    const [renaming, setRenaming] = useState(false);
    const [draft, setDraft] = useState(title);
    const [confirmingDelete, setConfirmingDelete] = useState(false);
    const [exportOpen, setExportOpen] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const { data: exportState, mutate: refreshExportState } =
        useDashboardExportState(dashboardId);
    const exportedVersion = exportState?.version ?? 0;

    const exportToApps = async (
        name: string,
        grouping: string,
        access: DashboardAccessInput | undefined
    ) => {
        await onExportToApps(name, grouping, access);
        setError(null);
        await refreshExportState();
    };

    const toggleShare = async () => {
        try {
            await onToggleShare?.(!shared);
            setError(null);
        } catch (failure) {
            setError(
                failure instanceof Error ? failure.message : 'Could not share'
            );
        }
    };

    const startRename = () => {
        setDraft(title);
        setRenaming(true);
    };

    const finishRename = async () => {
        const next = draft.trim();
        setRenaming(false);
        if (!next || next === title) return;
        try {
            await onRename(next);
            setError(null);
        } catch (failure) {
            setError(
                failure instanceof Error ? failure.message : 'Could not rename'
            );
        }
    };

    const confirmDelete = async () => {
        try {
            await onDelete();
        } catch (failure) {
            setError(
                failure instanceof Error ? failure.message : 'Could not delete'
            );
        }
    };

    if (renaming) {
        return (
            <Input
                autoFocus
                value={draft}
                maxLength={200}
                onChange={(event) => setDraft(event.target.value)}
                onBlur={finishRename}
                onKeyDown={(event) => {
                    if (event.key === 'Enter') finishRename();
                    if (event.key === 'Escape') setRenaming(false);
                }}
                aria-label="Dashboard name"
                className="w-56 h-8"
            />
        );
    }

    return (
        <>
            <div
                className={cn(
                    'group/title flex items-center gap-0.5 hover:bg-muted/60 rounded-md min-w-0',
                    menuOpen && 'bg-muted/60'
                )}
            >
                <Button
                    variant="ghost"
                    onClick={startRename}
                    title="Click to rename"
                    className="focus-visible:z-10 rounded-none rounded-l-md px-2.5 min-w-0 max-w-[37.5rem] h-8 overflow-hidden text-base leading-6"
                >
                    <span className="truncate">{title}</span>
                </Button>
                <div
                    className={cn(
                        'bg-border w-px h-6 transition-opacity duration-150',
                        menuOpen
                            ? 'opacity-100'
                            : 'opacity-0 group-hover/title:opacity-100 group-focus-within/title:opacity-100'
                    )}
                />
                <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
                    <DropdownMenuTrigger asChild>
                        <Button
                            variant="ghost"
                            size="icon-sm"
                            aria-label="Dashboard actions"
                            className="rounded-none rounded-r-md"
                        >
                            <ChevronDown />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                        align="start"
                        className="bg-background min-w-[calc(100%+1rem)]"
                    >
                        <div className="px-2 py-1.5 text-muted-foreground text-sm">
                            {relativeTime(updatedAt) && (
                                <p>Updated {relativeTime(updatedAt)}</p>
                            )}
                            {error && (
                                <p className="text-destructive">{error}</p>
                            )}
                        </div>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onSelect={startRename}>
                            <Pencil className="mr-2" />
                            Rename
                        </DropdownMenuItem>
                        <DropdownMenuItem
                            onSelect={() =>
                                window.open(
                                    `${getRouterBasename()}/dashboard/${encodeURIComponent(
                                        dashboardId
                                    )}`,
                                    '_blank',
                                    'noopener'
                                )
                            }
                        >
                            <ExternalLink className="mr-2" />
                            Open in new tab
                        </DropdownMenuItem>
                        {onToggleShare && (
                            <DropdownMenuItem onSelect={toggleShare}>
                                <Users className="mr-2" />
                                {shared
                                    ? 'Stop sharing'
                                    : 'Share on Organization artifacts'}
                            </DropdownMenuItem>
                        )}
                        <DropdownMenuItem
                            onSelect={() => {
                                setExportOpen(true);
                            }}
                        >
                            <LayoutGrid className="mr-2" />
                            {exportedVersion > 0
                                ? 'Update in BIG'
                                : 'Export to BIG'}
                            {exportedVersion > 0 && (
                                <span className="ml-auto pl-3 text-muted-foreground text-xs">
                                    v{exportedVersion}
                                </span>
                            )}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                            variant="destructive"
                            onSelect={() => setConfirmingDelete(true)}
                        >
                            <Trash2 className="mr-2" />
                            Delete dashboard
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
            <ExportToBigDialog
                open={exportOpen}
                dashboardId={dashboardId}
                title={title}
                onOpenChange={setExportOpen}
                onExport={exportToApps}
            />
            <AlertDialog
                open={confirmingDelete}
                onOpenChange={setConfirmingDelete}
            >
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            Delete this dashboard?
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            “{title}” is removed for good. Chats that show it
                            will no longer be able to open it.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={confirmDelete}>
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}
